import React, { useEffect, useState } from 'react';
import { X, Check, ChevronRight, RotateCcw, ClipboardList } from 'lucide-react';
import { questionsFor, scoreAnswers, TEST_LABEL } from '../../../utils/educationAssessments';
import { recordAttempt, attemptsFor } from '../../../utils/educationLibrary';

/*
 * Takes a pre-test or post-test: intro, one question per screen with the explanation shown
 * after answering, then a score with a review of every question. Full-screen layer; the
 * caller registers it with the Back stack.
 */

const AssessmentSheet = ({ collection: col, which, accent, onClose }) => {
  const questions = questionsFor(col, which);
  const previous = attemptsFor(col.id)[which];
  const [stage, setStage] = useState('intro'); // intro | question | results
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [chosen, setChosen] = useState(null); // option picked on the current question, before Next

  useEffect(() => { window.scrollTo(0, 0); }, [stage, index]);

  const q = questions[index];
  const start = () => { setAnswers({}); setIndex(0); setChosen(null); setStage('question'); };
  const pick = (optionId) => {
    if (chosen) return; // one pick per question; the explanation then shows
    setChosen(optionId);
    setAnswers((a) => ({ ...a, [q.id]: optionId }));
  };
  const next = () => {
    if (index + 1 < questions.length) { setIndex(index + 1); setChosen(null); return; }
    const { score, total } = scoreAnswers(questions, answers);
    recordAttempt(col.id, which, { score, total, answers, completedAt: Date.now() }, { title: col.title });
    setStage('results');
  };

  const label = TEST_LABEL[which];
  const { score, total } = stage === 'results' ? scoreAnswers(questions, answers) : { score: 0, total: questions.length };

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
      <div className="max-w-md mx-auto min-h-screen flex flex-col">
        <div className={`bg-gradient-to-br ${accent} text-white px-6 pt-5 pb-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-white/80">{label}</div>
              <div className="font-semibold truncate">{col.title}</div>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 rounded-full hover:bg-white/15"><X size={22} /></button>
          </div>
          {stage === 'question' && (
            <div className="mt-3 h-1.5 rounded-full bg-white/25 overflow-hidden" aria-hidden="true">
              <div className="h-full bg-white transition-all" style={{ width: `${((index + (chosen ? 1 : 0)) / questions.length) * 100}%` }} />
            </div>
          )}
        </div>

        <div className="px-6 py-6 flex-1">
          {stage === 'intro' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-center mx-auto mb-4"><ClipboardList size={26} /></div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{questions.length} {questions.length === 1 ? 'question' : 'questions'}</h2>
              {previous && previous.latest && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Last score {previous.latest.score} of {previous.latest.total}
                  {previous.best && previous.best.score !== previous.latest.score ? ` · best ${previous.best.score}` : ''}
                </p>
              )}
              <button onClick={start} className="mt-5 w-full py-3 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold active:scale-[0.98]">
                {previous ? 'Retake' : 'Start'}
              </button>
            </div>
          )}

          {stage === 'question' && q && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Question {index + 1} of {questions.length}</div>
              <h2 className="text-[17px] font-semibold leading-snug text-gray-900 dark:text-white">{q.stem}</h2>
              <div className="mt-4 space-y-2">
                {q.options.filter((o) => o.text && o.text.trim()).map((o, i) => {
                  const isChosen = chosen === o.id;
                  const isAnswer = o.id === q.answer;
                  let tone = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400';
                  if (chosen && isAnswer) tone = 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500';
                  else if (chosen && isChosen) tone = 'bg-red-50 dark:bg-red-900/30 border-red-400';
                  else if (chosen) tone = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70';
                  return (
                    <button
                      key={o.id}
                      onClick={() => pick(o.id)}
                      disabled={Boolean(chosen)}
                      className={`w-full text-left flex items-start gap-3 p-3.5 rounded-xl border-2 transition-colors ${tone}`}
                    >
                      <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${chosen && isAnswer ? 'bg-emerald-600 text-white' : chosen && isChosen ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        {chosen && isAnswer ? <Check size={14} strokeWidth={3} /> : chosen && isChosen ? <X size={14} strokeWidth={3} /> : String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-[15px] text-gray-900 dark:text-white leading-snug">{o.text}</span>
                    </button>
                  );
                })}
              </div>
              {chosen && (
                <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                  <div className={`text-sm font-semibold ${chosen === q.answer ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {chosen === q.answer ? 'Correct' : 'Incorrect'}
                  </div>
                  {q.explanation && <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">{q.explanation}</p>}
                  <button onClick={next} className="mt-4 w-full py-3 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold inline-flex items-center justify-center gap-1 active:scale-[0.98]">
                    {index + 1 < questions.length ? <>Next <ChevronRight size={16} /></> : 'See results'}
                  </button>
                </div>
              )}
            </div>
          )}

          {stage === 'results' && (
            <div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label} score</div>
                <div className="font-display text-5xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{score}<span className="text-2xl text-gray-400"> / {total}</span></div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{Math.round((score / Math.max(1, total)) * 100)}%</div>
                <div className="flex gap-2 mt-5">
                  <button onClick={start} className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-800 dark:text-gray-100 inline-flex items-center justify-center gap-1.5"><RotateCcw size={15} /> Retake</button>
                  <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold">Done</button>
                </div>
              </div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mt-6 mb-2">Review</h3>
              <div className="space-y-3">
                {questions.map((rq, i) => {
                  const right = answers[rq.id] === rq.answer;
                  const picked = rq.options.find((o) => o.id === answers[rq.id]);
                  const correct = rq.options.find((o) => o.id === rq.answer);
                  return (
                    <div key={rq.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-start gap-3">
                        <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${right ? 'bg-emerald-600' : 'bg-red-500'} text-white`}>{right ? <Check size={14} strokeWidth={3} /> : <X size={14} strokeWidth={3} />}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{i + 1}. {rq.stem}</div>
                          {!right && picked && <div className="text-sm text-red-600 dark:text-red-400 mt-1">You chose: {picked.text}</div>}
                          {correct && <div className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">Answer: {correct.text}</div>}
                          {rq.explanation && <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{rq.explanation}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentSheet;
